<template>
  <div class="ai-assistant-panel-container d-flex flex-column">
    <!-- Universal Header -->
    <v-card-title
      class="d-flex justify-space-between align-center px-6 pt-6 pb-4 mb-2"
      :class="{ 'is-chat-action': currentView !== ChatView.MAIN }"
    >
      <div class="d-flex align-center">
        <!-- Back button for ASK view only -->
        <v-btn
          v-if="currentView === ChatView.ASK"
          icon
          variant="text"
          size="small"
          @click="goBackToMain"
        >
          <v-icon size="24" icon="$chevronLeft" />
        </v-btn>

        <!-- AI Assistant icon (only for main view) -->
        <coro-icon
          v-if="currentView === ChatView.MAIN"
          :icon-name="CoroIcons.AI_ASSISTANT"
          class="ai-assistant-header-icon mr-2"
        />

        <!-- Dynamic title based on current view -->
        <div class="d-flex align-center">
          <span
            :class="{
              headline6: currentView === ChatView.MAIN,
              subtitle2: currentView !== ChatView.MAIN,
            }"
            >{{ getViewTitle }}</span
          >
          <!-- Beta badge for main AI Assistant title -->
          <v-chip
            v-if="currentView === ChatView.MAIN"
            size="x-small"
            variant="flat"
            class="beta-badge ml-2"
          >
            beta
          </v-chip>
        </div>
      </div>

      <div class="d-flex align-center ga-2">
        <!-- New message button for chat views -->
        <v-tooltip v-if="showChatView" open-delay="300" location="top">
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" icon variant="text" size="small" @click="startNewChat">
              <v-icon size="24" icon="$newMessage" />
            </v-btn>
          </template>
          <span>{{ $t("aiAssistant.chat.startNewChatTooltip") }}</span>
        </v-tooltip>

        <v-btn icon variant="text" size="small" @click="closePanel">
          <v-icon size="24" icon="$close" />
        </v-btn>
      </div>
    </v-card-title>

    <!-- Scrollable Content Area -->
    <div class="ai-assistant-content d-flex flex-column with-input">
      <!-- Show Chat View -->
      <ai-assistant-chat-view
        v-if="showChatView"
        class="pa2"
        :initial-message="initialMessage"
        :context="context"
        @close="closePanel"
        @back="goBackToMain"
      />

      <!-- Show Ask View -->
      <ai-assistant-ask-view
        v-else-if="currentView === ChatView.ASK"
        class="pa2"
        @close="closePanel"
        @back="goBackToMain"
        @show-chat="handleShowChat"
        @suggestion-selected="handleSuggestionSelected"
      />

      <!-- Show Session Failed Empty State -->
      <template v-else-if="sessionFailed">
        <v-card-text
          class="flex-grow-1 pa-6 d-flex flex-column align-center justify-center text-center"
        >
          <v-icon size="80" icon="$alertTriangle" />
          <div class="headline6 mb-2">
            {{ $t("aiAssistant.error.title") }}
          </div>
          <p class="body2 text-indigo-medium mb-6 max-width-error">
            {{ $t("aiAssistant.error.description") }}
          </p>
          <v-btn
            class="px-3"
            rounded
            variant="elevated"
            color="primary"
            size="large"
            @click="retrySessionCreation"
            :loading="loading"
          >
            <v-icon class="mr-1" icon="$retractXs" size="24" />
            {{ $t("aiAssistant.error.retry") }}
          </v-btn>
        </v-card-text>
      </template>

      <!-- Show Main View -->
      <template v-else>
        <!-- Content -->
        <v-card-text class="flex-grow-1 pa-6">
          <div v-if="loading" class="text-center">
            <!-- Circular Loader for main screen -->
            <v-progress-circular indeterminate color="primary" size="40" />
          </div>

          <div v-else>
            <!-- Greeting -->
            <div class="mb-12">
              <div class="headline5 mb-2">
                {{ $t("aiAssistant.greeting.title", { userName: getUserDisplayName }) }}
              </div>
              <p class="subtitle2 text-indigo-medium">{{ $t("aiAssistant.greeting.subtitle") }}</p>
            </div>

            <!-- Action Cards -->
            <div>
              <v-card
                class="ai-assistant-action-card mb-4"
                data-testid="ai-assistant-summary-card"
                variant="outlined"
                @click="handleSummaryAction"
              >
                <v-card-text class="pa-4">
                  <div class="d-flex align-center ga-3">
                    <div class="summary-icon">
                      <v-icon size="24" icon="$aiSummary" />
                    </div>
                    <div>
                      <div class="subtitle2">
                        {{ getSummaryTitle }}
                      </div>
                      <div class="body3 text-indigo-medium">
                        {{ getSummarySubTitle }}
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="ai-assistant-action-card mb-4"
                data-testid="ai-assistant-security-gaps-card"
                variant="outlined"
                @click="handleSecurityGapsOverviewAction"
              >
                <v-card-text class="pa-4">
                  <div class="d-flex align-center ga-3">
                    <div class="summary-icon">
                      <v-icon size="24" icon="$protectionIssueXs" />
                    </div>
                    <div>
                      <div class="subtitle2">
                        {{ $t("aiAssistant.securityGapsSummary.title") }}
                      </div>
                      <div class="body3 text-indigo-medium">
                        {{ $t("aiAssistant.securityGapsSummary.subtitle") }}
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card
                class="ai-assistant-action-card"
                data-testid="ai-assistant-ask-card"
                variant="outlined"
                @click="handleAskAction"
              >
                <v-card-text class="pa-4">
                  <div class="d-flex align-center ga-3">
                    <div class="ask-icon">
                      <v-icon size="24" icon="$ask" />
                    </div>
                    <div>
                      <div class="subtitle2">
                        {{ $t("aiAssistant.actions.ask.title") }}
                      </div>
                      <div class="body3 text-indigo-medium">
                        {{ $t("aiAssistant.actions.ask.subtitle") }}
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-card-text>
      </template>
    </div>

    <!-- Fixed Input Section for all views -->
    <ai-assistant-input
      v-if="!sessionFailed"
      v-model="currentMessage"
      :placeholder="$t('aiAssistant.ask.inputPlaceholder')"
      :disabled="loading || isLastMessageApprovalCard"
      :is-session-inactive="currentView === ChatView.CHAT && isSessionInactive"
      @send-message="handleSendMessage"
      @start-new-chat="startNewChat"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, type PropType } from "vue";
import { storeToRefs } from "pinia";
import { useAiAssistantStore } from "@/_store/ai-assistant.module";
import { useMyAccountStore } from "@/_store/my-account.module";
import { useI18n } from "vue-i18n";
import AiAssistantAskView from "./AiAssistantAskView.vue";
import AiAssistantChatView from "./AiAssistantChatView.vue";
import AiAssistantInput from "./AiAssistantInput.vue";
import { CoroIcons } from "@/constants/coro-icon.ts";
import { ChatView } from "@/constants/ai-assistant.ts";
import CoroIcon from "@/components/CoroIcon.vue";
import { AppContext } from "@/types.ts";

export default defineComponent({
  props: {
    context: {
      type: String as PropType<AppContext>,
      required: true,
    },
  },
  components: {
    CoroIcon,
    AiAssistantAskView,
    AiAssistantChatView,
    AiAssistantInput,
  },
  emits: ["close"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const aiAssistantStore = useAiAssistantStore();
    const { loading, lastMessage, isLastMessageApprovalCard } = storeToRefs(aiAssistantStore);
    const myAccountStore = useMyAccountStore();
    const currentView = ref<ChatView>(ChatView.MAIN);
    const chatMessage = ref<string>("");
    const currentMessage = ref<string>("");
    const sessionFailed = ref<boolean>(false);

    const getViewTitle = computed(() => {
      switch (currentView.value) {
        case ChatView.SUMMARY:
          return props.context === AppContext.MSP
            ? t("aiAssistant.summary.mspTitle")
            : t("aiAssistant.summary.title");
        case ChatView.SECURITY_GAPS_SUMMARY:
          return t("aiAssistant.securityGapsSummary.title");
        case ChatView.ASK:
        case ChatView.CHAT:
          return t("aiAssistant.ask.title");
        default:
          return t("aiAssistant.titleExtended");
      }
    });

    const getSummaryTitle = computed(() => {
      return props.context === AppContext.MSP
        ? t("aiAssistant.actions.summary.mspTitle")
        : t("aiAssistant.actions.summary.title");
    });

    const getSummarySubTitle = computed(() => {
      return props.context === AppContext.MSP
        ? t("aiAssistant.actions.summary.mspSubtitle")
        : t("aiAssistant.actions.summary.subtitle");
    });

    const getUserDisplayName = computed(() => {
      const profileData = myAccountStore.myAccount.profileData;
      const firstName = profileData.firstName;
      const lastName = profileData.lastName;

      if (firstName) {
        return firstName;
      } else if (lastName) {
        return lastName;
      }
      return t("aiAssistant.defaultUser"); // fallback value
    });

    const isSessionInactive = computed(() => {
      return lastMessage.value?.sessionInfo?.active === false;
    });

    const initialMessage = computed(() => {
      if (currentView.value === ChatView.SUMMARY) {
        return t("aiAssistant.summary.generateMessage");
      }
      if (currentView.value === ChatView.SECURITY_GAPS_SUMMARY) {
        return t("aiAssistant.securityGapsSummary.generateMessage");
      }

      return chatMessage.value;
    });

    const showChatView = computed(() => {
      return [ChatView.CHAT, ChatView.SUMMARY, ChatView.SECURITY_GAPS_SUMMARY].includes(
        currentView.value
      );
    });

    const closePanel = () => {
      emit("close");
    };

    const handleSummaryAction = async () => {
      currentView.value = ChatView.SUMMARY;

      await aiAssistantStore.sendMessage({
        message: t("aiAssistant.summary.generateMessage"),
      });
    };

    const handleSecurityGapsOverviewAction = async () => {
      currentView.value = ChatView.SECURITY_GAPS_SUMMARY;

      await aiAssistantStore.sendMessage({
        message: t("aiAssistant.securityGapsSummary.generateMessage"),
      });
    };

    const handleAskAction = () => {
      currentView.value = ChatView.ASK;
    };

    const goBackToMain = () => {
      currentView.value = ChatView.MAIN;
    };

    const handleShowChat = (message: string) => {
      chatMessage.value = message;
      currentView.value = ChatView.CHAT;
    };

    const handleSuggestionSelected = (suggestion: string) => {
      currentMessage.value = suggestion;
      sendMessage();
    };

    const startNewChat = async () => {
      currentView.value = ChatView.MAIN;
      aiAssistantStore.$reset();
      await aiAssistantStore.createSession();
    };

    const retrySessionCreation = async () => {
      sessionFailed.value = false;
      const sessionCreated = await aiAssistantStore.ensureActiveSession();

      if (!sessionCreated) {
        sessionFailed.value = true;
      }
    };

    const sendMessage = async () => {
      if (!currentMessage.value.trim()) return;

      const message = currentMessage.value.trim();
      currentMessage.value = "";

      handleShowChat(message);
      await aiAssistantStore.sendMessage({
        message: message,
      });
    };

    const handleSendMessage = async (message: string) => {
      // If we're in main view, transition to chat view (like "Ask Coro" button)
      if (currentView.value === ChatView.MAIN) {
        currentView.value = ChatView.CHAT;
        chatMessage.value = message;
      } else {
        handleShowChat(message);
      }

      await aiAssistantStore.sendMessage({
        message: message,
      });
    };

    onMounted(async () => {
      const sessionCreated = await aiAssistantStore.ensureActiveSession();

      if (!sessionCreated) {
        sessionFailed.value = true;
        return;
      }

      // If we have an active session, check for existing messages
      if (aiAssistantStore.hasActiveSession) {
        // Load message history if we don't have any messages in memory
        if (aiAssistantStore.messages.length === 0) {
          await aiAssistantStore.getMessageHistory();
        }

        // If we have messages after loading history, navigate to chat view
        if (aiAssistantStore.messages.length > 0) {
          currentView.value = ChatView.CHAT;
        }
      }
    });

    return {
      loading,
      currentView,
      chatMessage,
      currentMessage,
      sessionFailed,
      getViewTitle,
      getUserDisplayName,
      isSessionInactive,
      closePanel,
      handleSummaryAction,
      handleAskAction,
      handleSecurityGapsOverviewAction,
      goBackToMain,
      handleShowChat,
      handleSuggestionSelected,
      startNewChat,
      retrySessionCreation,
      sendMessage,
      handleSendMessage,
      CoroIcons,
      ChatView,
      getSummaryTitle,
      getSummarySubTitle,
      initialMessage,
      showChatView,
      isLastMessageApprovalCard,
    };
  },
});
</script>

<style lang="scss" scoped>
.icon-protection-issue-xs {
  &:before {
    color: rgb(var(--v-theme-labels-red-full));
  }
}

.ai-assistant-action-card {
  cursor: pointer;
  border: 1px solid rgb(var(--v-theme-indigo-light)) !important;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.ai-assistant-panel-container {
  height: 100%;
  border-radius: 8px;
  border: 2px solid var(rgba(205, 141, 229, 0.25));
  background:
    linear-gradient(
      180deg,
      rgba(205, 141, 229, 0.2) -6.64%,
      rgba(22, 164, 246, 0.08) 22.06%,
      rgba(255, 255, 255, 0) 50.75%
    ),
    #fff;
}

.ai-assistant-content {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;

  &.with-input {
    padding-bottom: 0;
  }
}

.ai-assistant-header-icon {
  width: 40px;
  height: 40px;
}

.ai-error-icon {
  width: 64px;
  height: 64px;
  opacity: 0.6;
}

.max-width-error {
  max-width: 300px;
}

.is-chat-action {
  background: rgb(var(--v-theme-white)) !important;
}

.icon-alert-triangle-l:before {
  color: rgb(var(--v-theme-red-dark)) !important;
}

.icon-mail-retract-xs:before {
  color: rgb(var(--v-theme-white)) !important;
}

.beta-badge {
  background-color: rgba(var(--v-theme-indigo-light), 0.4) !important;
  color: rgb(var(--v-theme-indigo-base)) !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  height: 20px !important;
  border-radius: 4px !important;
}
</style>
